import { Link } from '@tanstack/react-router';
import { toMajor } from '../../contracts/money';
import {
  ArrowRight,
  Brush,
  Download,
  ExternalLink,
  Plus,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { formatInt } from '../../lib/format';
import {
  useCurrentReseller, useResellerCustomers, useResellerKpis, useResellerRevenue, useResellerActivity,
} from '../../api/reseller';
import { formatDate } from '../../lib/format';

/** Humanize an audit action key ("subscription.past_due" → "Subscription past due"). */
function humanizeAction(action: string): string {
  const s = action.replace(/[._]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Reseller home — KPI strip, recent activity, revenue mini-chart, quick actions. */
export function ResellerOverview() {
  const { data: currentReseller } = useCurrentReseller();
  const { data: resellerCustomers = [] } = useResellerCustomers();
  const { data: resellerKpis } = useResellerKpis();
  const { data: resellerRevenue } = useResellerRevenue();
  const { data: activity = [] } = useResellerActivity();
  if (!currentReseller || !resellerKpis || !resellerRevenue) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading overview…
      </div>
    );
  }
  const max = Math.max(...resellerRevenue.monthly.map((m) => m.bdt));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow={currentReseller.displayName}
        title="Overview"
        subtitle={`How ${currentReseller.name} is doing this period.`}
        action={
          <Link to="/reseller/customers/new">
            <Button size="sm" iconLeft={<Plus className="w-4 h-4" />}>
              New customer
            </Button>
          </Link>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <KpiTile
          label="Customers"
          value={<span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{formatInt(resellerKpis.customers)}</span>}
          subtitle="businesses under your reseller"
        />
        <KpiTile
          label="Trials"
          value={<span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{resellerKpis.trials}</span>}
          subtitle="active free trials"
        />
        <KpiTile
          label="Past due"
          value={<span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{resellerKpis.pastDue}</span>}
          subtitle="awaiting payment"
          tone="warning"
        />
        <KpiTile
          label="MRR"
          value={
            <Money minor={resellerKpis.mrrMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
          }
          subtitle="recurring revenue"
        />
        <KpiTile
          label="Churn"
          value={<span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{resellerKpis.churnPct}%</span>}
          subtitle="canceled in the last 30 days"
        />
      </div>

      {/* Recent activity */}
      <Card className="p-5 sm:p-6 mb-6">
        <CardHeader
          title="Recent customer activity"
          subtitle="The last few signups, payments, and dunning events"
          action={
            <Link
              to="/reseller/customers"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-brand-dm hover:underline"
            >
              View all
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          }
        />
        {activity.length === 0 ? (
          <p className="py-6 text-sm text-zinc-500 dark:text-zinc-400">
            No recent activity yet — signups, payments, and dunning events will appear here.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-700 -mx-2">
            {activity.slice(0, 6).map((a) => (
              <ActivityRow
                key={a.id}
                tone={a.severity === 'critical' || a.severity === 'warning' ? 'warning' : 'info'}
                primary={humanizeAction(a.action)}
                secondary={a.target ?? a.actorEmail ?? ''}
                timestamp={a.occurredAt ? formatDate(a.occurredAt, { compact: true }) : ''}
                badge={a.category ? <Badge tone="neutral">{a.category}</Badge> : undefined}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* Revenue + Quick actions */}
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        <Card className="lg:col-span-2 p-5 sm:p-6">
          <CardHeader
            title="Revenue (last 12 months)"
            subtitle={
              <span className="tabular-nums">
                Total{' '}
                <Money
                  minor={resellerRevenue.grossPaidMinor}
                  hideCents
                  tone="neutral"
                  className="font-medium text-zinc-700 dark:text-zinc-200"
                />{' '}
                · Your share{' '}
                <Money
                  minor={resellerRevenue.yourCommissionMinor}
                  hideCents
                  tone="neutral"
                  className="font-medium text-zinc-700 dark:text-zinc-200"
                />
              </span>
            }
          />
          <div className="flex items-end gap-1 sm:gap-2 h-40 mt-2">
            {resellerRevenue.monthly.map((m) => {
              const pct = (m.bdt / max) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 group">
                  <div className="relative w-full flex items-end h-32">
                    <div
                      className="w-full rounded-t-sm bg-brand-subtle dark:bg-brand-dm-subtle/50 group-hover:bg-brand dark:group-hover:bg-brand-dm transition-colors"
                      style={{ height: `${pct}%` }}
                      title={`${m.month}: ৳${formatInt(toMajor(m.bdt))}`}
                    />
                  </div>
                  <span className="text-[10px] text-zinc-500 dark:text-zinc-400 tabular-nums tracking-tight">
                    {m.month.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Quick actions" />
          <div className="space-y-1 -mx-2">
            <QuickAction icon={<Plus className="w-4 h-4" />} label="Create customer manually" to="/reseller/customers/new" />
            <QuickAction icon={<UserPlus className="w-4 h-4" />} label="Invite staff" to="/reseller/staff" />
            <QuickAction icon={<Download className="w-4 h-4" />} label="Export commissions" to="/reseller/revenue" />
            <QuickAction icon={<Brush className="w-4 h-4" />} label="Branding" to="/reseller/branding" />
            <QuickAction icon={<Users className="w-4 h-4" />} label="View all customers" to="/reseller/customers" />
          </div>
        </Card>
      </div>

      {/* Footnote */}
      {/* This used to read "visible in the demo dataset" — shown to production resellers
          about their REAL customers, inviting them to distrust their own numbers. */}
      <p className="mt-6 text-xs text-zinc-500 dark:text-zinc-400">
        Showing {formatInt(resellerCustomers.length)} of your {formatInt(resellerKpis.customers)} businesses.
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */

function KpiTile({
  label,
  value,
  delta,
  deltaSuffix,
  subtitle,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  delta?: number;
  deltaSuffix?: string;
  subtitle?: string;
  tone?: 'default' | 'warning';
}) {
  const positive = (delta ?? 0) >= 0;
  return (
    <Card className={cn('p-4 sm:p-5', tone === 'warning' && 'border-amber-200 dark:border-amber-900/60')}>
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-2xl sm:text-3xl font-semibold">{value}</div>
      {delta !== undefined ? (
        <div
          className={cn(
            'mt-1 inline-flex items-center gap-1 text-xs tabular-nums',
            positive ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300',
          )}
        >
          {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(delta).toFixed(1)}%
          {deltaSuffix && <span className="text-zinc-500 dark:text-zinc-400 ml-1">{deltaSuffix}</span>}
        </div>
      ) : (
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
      )}
    </Card>
  );
}

function ActivityRow({
  tone,
  primary,
  secondary,
  timestamp,
  badge,
}: {
  tone: 'success' | 'warning' | 'info';
  primary: string;
  secondary: string;
  timestamp: string;
  badge?: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 px-2 py-3">
      <span
        className={cn(
          'w-2 h-2 rounded-full flex-shrink-0',
          tone === 'success' && 'bg-emerald-500',
          tone === 'warning' && 'bg-amber-500',
          tone === 'info' && 'bg-sky-500',
        )}
      />
      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{primary}</span>
        <span className="text-sm text-zinc-600 dark:text-zinc-300 truncate">{secondary}</span>
      </div>
      {badge}
      <span className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums whitespace-nowrap">{timestamp}</span>
    </li>
  );
}

function QuickAction({
  icon,
  label,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
    >
      <span className="text-zinc-500 dark:text-zinc-400">{icon}</span>
      <span className="flex-1">{label}</span>
      <ExternalLink className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
    </Link>
  );
}
